import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../components/AuthProvider';
import { supabase } from '../../lib/auth';
import { getActiveSDRs } from '../../lib/api';
import { getCreatedOnDate, getCreatedBetween, getSdrCallActivity, getMinariActivity, type DayRdv, type SdrActivity, type SdrActivityResult, type MinariActivity } from '../../lib/refonteApi';
import StatusBadge from '../../components/refonte/StatusBadge';
import RdvDetail from '../../components/refonte/RdvDetail';
import { SkeletonRows } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';
import AddRdvModal from '../../components/refonte/AddRdvModal';

const RDV_TARGET = 3;
// Objectifs d'appels par jour, ajustables. Cold call = 30, GTM (full-stack) = 15.
const CALL_TARGET = { cold_call: 30, gtm: 15 };
const EMPTY_ACT: SdrActivity = { appels: 0, conversations: 0, connectes: 0, voicemails: 0, connectMin: 0 };
const WD = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven'];
const toISODate = (d: Date) => d.toISOString().split('T')[0];
const fmtDay = (d: Date) => d.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long' });
const mondayOf = (d: Date) => { const x = new Date(d.getFullYear(), d.getMonth(), d.getDate()); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const fmtShort = (d: Date) => d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });

const Stat: React.FC<{ value: React.ReactNode; label: string; tone?: string }> = ({ value, label, tone }) => (
  <div className="text-center px-3 first:pl-0">
    <div className={`font-mono text-[18px] font-bold leading-none ${tone || ''}`}>{value}</div>
    <div className="font-mono text-[9px] uppercase tracking-widest text-faint mt-1">{label}</div>
  </div>
);

// Petite tuile de stat (bloc d'activité).
const Tile: React.FC<{ v: React.ReactNode; l: string; tone?: string }> = ({ v, l, tone }) => (
  <div className="text-center min-w-[54px]">
    <div className={`font-mono text-[16px] font-bold leading-none tabular-nums ${tone || 'text-ink'}`}>{v}</div>
    <div className="font-mono text-[8.5px] uppercase tracking-wider text-faint mt-1">{l}</div>
  </div>
);

// Bloc d'activité d'appels — CONNECTÉS en avant vs objectif (l'objectif 30/15 porte sur les
// connectés, pas les composés). Composés = volume brut, en petit à côté.
// m = données Minari ; fallback = call_logs (connectés+VM) si Minari indispo.
const ActivityBlock: React.FC<{ m?: MinariActivity; fallback: SdrActivity; target: number }> = ({ m, fallback, target }) => {
  const composes = m ? m.composes : fallback.appels;
  const connectes = m ? m.connectes : fallback.connectes;
  const conversations = m ? m.conversations : fallback.conversations;
  const connectMin = m ? m.sessionMin : fallback.connectMin;
  const meetings = m ? m.meetings : 0;
  const pct = target > 0 ? Math.min(100, Math.round((connectes / target) * 100)) : 0;
  const ok = connectes >= target;
  const src = m ? 'Minari (API directe)' : 'call_logs (connectés + VM seulement — Minari indisponible)';
  return (
    <div className="rounded-lg border border-line bg-panel-2 px-3.5 py-3">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="shrink-0" title={src}>
          <div className="flex items-baseline gap-1.5">
            <span className={`font-mono text-[28px] font-bold leading-none tabular-nums ${ok ? 'text-st-ok' : connectes === 0 ? 'text-st-bad' : 'text-hook-rose'}`}>{connectes}</span>
            <span className="font-mono text-[13px] text-faint">/ {target}</span>
          </div>
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="w-32 h-2 rounded bg-raise overflow-hidden inline-block"><span className={`block h-full ${ok ? 'bg-st-ok' : 'bg-hook-rose'}`} style={{ width: `${pct}%` }} /></span>
            <span className="font-mono text-[9px] uppercase tracking-wider text-dim">connectés{!m && ' *'}</span>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-end gap-4 flex-wrap">
          <Tile v={composes} l="composés" />
          <Tile v={conversations} l="conversations" />
          <Tile v={`${connectMin}′`} l="connexion" />
          <Tile v={meetings} l="RDV Minari" tone={meetings > 0 ? 'text-st-ok' : 'text-dim'} />
        </div>
      </div>
    </div>
  );
};

// Page PRODUCTION SDR (prise de RDV du jour, par SDR classés) — issue du découpage de Day to Day.
const ProductionSdrPage: React.FC = () => {
  const { profile } = useAuth();
  const role = profile?.role || 'admin';
  const [mode, setMode] = useState<'jour' | 'semaine'>('jour');
  const [date, setDate] = useState<Date>(new Date());
  const [created, setCreated] = useState<DayRdv[]>([]);
  const [sdrs, setSdrs] = useState<Array<{ id: string; name: string; client_name: string }>>([]);
  const [act, setAct] = useState<SdrActivityResult>({ byId: {}, byClient: {}, teamById: {}, missionsById: {} });
  const [min, setMin] = useState<Record<string, MinariActivity>>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [selId, setSelId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const dateStr = toISODate(date);
  // Semaine ouvrée (lun→ven) contenant la date courante — pour la vue semaine (demande Romain + Baptiste).
  const week = useMemo(() => { const mon = mondayOf(date); const days = Array.from({ length: 5 }, (_, i) => { const d = new Date(mon); d.setDate(mon.getDate() + i); return d; }); return { days, from: toISODate(days[0]), to: toISODate(days[4]) }; }, [date]);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const [from, to] = mode === 'jour' ? [dateStr, dateStr] : [week.from, week.to];
      const [cre, active, activity, minari] = await Promise.all([
        mode === 'jour' ? getCreatedOnDate(dateStr) : getCreatedBetween(week.from, week.to),
        getActiveSDRs(date),
        getSdrCallActivity(from, to),
        getMinariActivity(from, to),
      ]);
      setCreated(cre);
      setSdrs((active ?? []).map((s: any) => ({ id: s.sdr_id, name: s.sdr_name, client_name: s.client_name })));
      setAct(activity);
      setMin(minari);
    } catch (e: any) { setErr(e.message); }
    setLoading(false);
  }, [mode, dateStr, week.from, week.to, date]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const ch = supabase.channel(`prod_${mode}_${dateStr}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'appointments' }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [mode, dateStr, load]);

  useEffect(() => { if (mode === 'jour' && created.length && !created.some(r => r.id === selId)) setSelId(created[0].id); }, [mode, created, selId]);

  const shiftDay = (delta: number) => {
    const d = new Date(date);
    if (mode === 'semaine') { d.setDate(d.getDate() + delta * 7); }
    else {
      d.setDate(d.getDate() + delta);
      if (d.getDay() === 0) d.setDate(d.getDate() + (delta > 0 ? 1 : -2));
      if (d.getDay() === 6) d.setDate(d.getDate() + (delta > 0 ? 2 : -1));
    }
    setDate(d); setSelId(null);
  };

  // Dédup par SDR (getActiveSDRs renvoie 1 ligne par SDR×client → un GTM apparaît N fois).
  const ranked = useMemo(() => {
    const m = new Map<string, { id: string; name: string; clients: string[] }>();
    for (const s of sdrs) {
      if (!m.has(s.id)) m.set(s.id, { id: s.id, name: s.name, clients: [] });
      const e = m.get(s.id)!;
      if (s.client_name && !e.clients.includes(s.client_name)) e.clients.push(s.client_name);
    }
    // Inclure les SDR qui ont booké sur la période mais absents de la liste active (surtout en semaine).
    for (const c of created) {
      if (c.sdr_id && !m.has(c.sdr_id)) m.set(c.sdr_id, { id: c.sdr_id, name: (c.sdr as any)?.sales_name || 'SDR', clients: [] });
    }
    return [...m.values()]
      .map(s => ({ ...s, mine: created.filter(c => c.sdr_id === s.id) }))
      .sort((a, b) => b.mine.length - a.mine.length);
  }, [sdrs, created]);
  const sel = created.find(r => r.id === selId) || null;
  const hasMinari = Object.keys(min).length > 0;
  const totComposes = hasMinari
    ? Object.values(min).reduce((s, x) => s + x.composes, 0)
    : Object.values(act.byId).reduce((s, x) => s + x.appels, 0);
  const totConnectes = hasMinari
    ? Object.values(min).reduce((s, x) => s + x.connectes, 0)
    : Object.values(act.byId).reduce((s, x) => s + x.connectes, 0);
  const totConv = hasMinari
    ? Object.values(min).reduce((s, x) => s + x.conversations, 0)
    : Object.values(act.byId).reduce((s, x) => s + x.conversations, 0);
  // Équipe d'un SDR : prospecting_team si défini, sinon déduit (plusieurs clients = GTM).
  const grpOf = (s: { id: string; clients: string[] }) =>
    (act.teamById[s.id] === 'gtm' || (act.teamById[s.id] == null && s.clients.length > 1)) ? 'gtm' : 'cold';
  const baseOf = (g: string) => g === 'gtm' ? CALL_TARGET.gtm : CALL_TARGET.cold_call;
  const todayISO = toISODate(new Date());

  type Row = { id: string; name: string; clients: string[]; mine: DayRdv[] };
  // Carte SDR — vue SEMAINE (avance jour par jour + bloc d'appels).
  const weekCard = (s: Row, i: number) => {
    const total = s.mine.length;
    const g = grpOf(s);
    const callTarget = baseOf(g) * (act.missionsById[s.id] || 1);
    return (
      <div key={s.id} className="border border-line rounded-lg p-3">
        <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
          <span className="text-[14px] font-semibold">
            <span className="font-mono text-faint mr-1.5">#{i + 1}</span>{s.name}
            {g === 'gtm' && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-st-ok/10 text-st-ok ml-2">GTM · {s.clients.length} clients</span>}
          </span>
          <span className="font-mono text-[12px] font-bold">{total}<span className="text-faint">/{RDV_TARGET * 5}</span> RDV</span>
        </div>
        <div className="mb-2"><ActivityBlock m={min[s.id]} fallback={act.byId[s.id] || EMPTY_ACT} target={callTarget} /></div>
        <div className="grid grid-cols-5 gap-1.5">
          {week.days.map((d, di) => {
            const iso = toISODate(d);
            const n = s.mine.filter(m => toISODate(new Date(m.created_at || iso)) === iso).length;
            const isPast = iso < todayISO;
            const tone = n >= RDV_TARGET ? 'text-st-ok' : isPast ? (n === 0 ? 'text-st-bad' : 'text-st-warn') : 'text-dim';
            return (
              <div key={di} className={`rounded-md border py-1.5 text-center ${iso === todayISO ? 'border-ink bg-panel-2' : 'border-line'}`}>
                <div className="font-mono text-[9px] uppercase tracking-widest text-faint">{WD[di]} {d.getDate()}</div>
                <div className={`font-mono text-[17px] font-bold leading-none mt-0.5 ${tone}`}>{n}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  // Carte SDR — vue JOUR (bloc d'appels + RDV par client).
  const jourCard = (s: Row, i: number) => {
    const total = s.mine.length;
    const g = grpOf(s);
    const base = baseOf(g);
    const callTarget = base * (act.missionsById[s.id] || 1);
    const groups = new Map<string, DayRdv[]>();
    for (const cl of s.clients) groups.set(cl, []);
    for (const m of s.mine) { const k = (m.clients as any)?.name || 'Sans client'; if (!groups.has(k)) groups.set(k, []); groups.get(k)!.push(m); }
    return (
      <div key={s.id} className="border border-line rounded-lg p-3">
        <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
          <span className="text-[14px] font-semibold">
            <span className="font-mono text-faint mr-1.5">#{i + 1}</span>{s.name}
            {g === 'gtm' && <span className="font-mono text-[9px] px-1.5 py-0.5 rounded bg-st-ok/10 text-st-ok ml-2">GTM · {s.clients.length} clients</span>}
          </span>
          <span className="font-mono text-[12px] font-bold">{total} RDV</span>
        </div>
        <div className="mb-2"><ActivityBlock m={min[s.id]} fallback={act.byId[s.id] || EMPTY_ACT} target={callTarget} /></div>
        <div className="space-y-1.5">
          {[...groups.entries()].map(([cl, rdvs]) => (
            <div key={cl} className="flex items-start gap-2 flex-wrap">
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-raise text-dim w-32 shrink-0 truncate" title={cl}>{cl}</span>
              {(() => { const ca = act.byClient[`${s.id}|${cl.toLowerCase().trim()}`]; const okc = (ca?.connectes || 0) >= base; return <span className={`font-mono text-[10px] w-20 shrink-0 tabular-nums ${okc ? 'text-st-ok' : (ca?.connectes || 0) === 0 ? 'text-st-bad' : 'text-st-warn'}`} title={`Appels CONNECTÉS sur ${cl} / objectif ${base} (détail par client = call_logs)`}>📞 {ca?.connectes || 0}/{base}</span>; })()}
              <span className="font-mono text-[11px] text-dim w-12 shrink-0 tabular-nums">{rdvs.length} RDV</span>
              <div className="flex-1 flex flex-wrap gap-1.5">
                {rdvs.length === 0 ? <span className="text-[11px] text-faint">—</span> : rdvs.map(m => (
                  <button key={m.id} onClick={() => setSelId(m.id)}
                    className={`inline-flex items-center gap-1.5 text-[11.5px] border rounded-lg px-2 py-1 transition ${selId === m.id ? 'border-ink bg-panel-2' : 'border-line hover:border-ink hover:bg-panel-2'}`}>
                    <span className="font-mono text-faint">{m.time?.slice(0, 5) || '—'}</span>
                    <span className="truncate max-w-[110px]">{m.prospect_name || 'Prospect'}</span>
                    <StatusBadge status={m.lifecycle_status ?? null} />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
        <h1 className="text-[21px] font-bold tracking-tight">Production SDR</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setAddOpen(true)} className="text-[12.5px] font-semibold px-3 h-8 rounded-lg bg-hook-rose text-white hover:opacity-90">+ Ajouter un RDV</button>
          <span className="w-px h-5 bg-line-2 mx-0.5" />
          <div className="flex gap-1 bg-raise rounded-lg p-0.5">
            {(['jour', 'semaine'] as const).map(mo => (
              <button key={mo} onClick={() => { setMode(mo); setSelId(null); }} className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md capitalize ${mode === mo ? 'bg-panel shadow-sm text-ink' : 'text-dim hover:text-ink'}`}>{mo}</button>
            ))}
          </div>
          <button onClick={() => shiftDay(-1)} aria-label="Précédent" className="w-8 h-8 rounded-lg border border-line-2 hover:border-ink">‹</button>
          <span className="font-mono text-[12px] text-dim capitalize min-w-[180px] text-center">{mode === 'jour' ? fmtDay(date) : `Semaine du ${fmtShort(week.days[0])} au ${fmtShort(week.days[4])}`}</span>
          <button onClick={() => shiftDay(1)} aria-label="Suivant" className="w-8 h-8 rounded-lg border border-line-2 hover:border-ink">›</button>
        </div>
      </div>
      <p className="text-dim text-[13px] mb-5">{mode === 'jour' ? <>Prise de RDV du jour, par SDR · objectif {RDV_TARGET}/jour</> : <>Prise de RDV de la semaine, par SDR et par jour · objectif {RDV_TARGET}/jour ({RDV_TARGET * 5}/semaine)</>} · temps réel {loading && '· maj…'}</p>

      {err && <div className="mb-4 text-[13px] text-st-bad bg-st-bad/10 border border-st-bad/20 rounded-lg px-3 py-2 font-mono">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4 lg:h-[80vh] items-stretch">
        <div className="bg-panel border border-line rounded-card p-5 flex flex-col overflow-hidden">
          <div className="inline-flex divide-x divide-line border border-line rounded-lg py-2.5 mb-4 bg-panel-2 shrink-0 self-start">
            <Stat value={ranked.length} label="SDR en prod" />
            <Stat value={created.length} label={mode === 'jour' ? 'RDV posés auj.' : 'RDV posés (sem.)'} tone="text-hook-rose" />
            <Stat value={ranked.length * RDV_TARGET * (mode === 'jour' ? 1 : 5)} label="Objectif" />
            <Stat value={created.filter(c => c.lifecycle_status === 'held').length} label="Déjà honorés" tone="text-st-ok" />
            <Stat value={totConnectes} label={mode === 'jour' ? 'Connectés auj.' : 'Connectés (sem.)'} tone="text-hook-rose" />
            <Stat value={totComposes} label={mode === 'jour' ? 'Composés auj.' : 'Composés (sem.)'} />
            <Stat value={totConv} label="Conversations >30s" />
          </div>
          <div className="flex-1 overflow-y-auto -mx-1 px-1">
            {loading ? <SkeletonRows count={4} /> : ranked.length === 0 ? (
              <EmptyState title="Aucun SDR planifié" hint="Personne en journée de prospection ce jour." />
            ) : (
              <div className="space-y-5">
                {(['cold', 'gtm'] as const).map(g => {
                  const list = ranked.filter(s => grpOf(s) === g);
                  if (!list.length) return null;
                  const gConn = hasMinari
                    ? list.reduce((n, s) => n + (min[s.id]?.connectes || 0), 0)
                    : list.reduce((n, s) => n + (act.byId[s.id]?.connectes || 0), 0);
                  const gTgt = list.reduce((n, s) => n + baseOf(g) * (act.missionsById[s.id] || 1), 0);
                  const gOk = gConn >= gTgt;
                  return (
                    <section key={g}>
                      <div className="flex items-center justify-between mb-2 px-0.5">
                        <h3 className="text-[12px] font-bold uppercase tracking-wider text-dim">
                          {g === 'gtm' ? '🚀 GTM' : '☎️ Cold Call'}
                          <span className="font-mono text-faint ml-1.5">· {list.length}</span>
                        </h3>
                        <span className="font-mono text-[11px] font-bold" title={`Connectés cumulés / objectif de l'équipe ${g === 'gtm' ? 'GTM' : 'Cold Call'}`}>
                          <span className={gOk ? 'text-st-ok' : 'text-dim'}>{gConn}</span>
                          <span className="text-faint">/{gTgt} connectés</span>
                        </span>
                      </div>
                      <div className="space-y-2.5">
                        {list.map((s, i) => mode === 'semaine' ? weekCard(s, i) : jourCard(s, i))}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="bg-panel border border-line rounded-card p-5 overflow-y-auto">
          {mode === 'semaine'
            ? <div className="h-full grid place-items-center"><EmptyState title="Vue semaine" hint="Repère qui est en retard sur la semaine (rouge = 0 RDV un jour passé). Repasse en « Jour » pour ouvrir et statuer un RDV." /></div>
            : sel ? <RdvDetail rdv={sel} role={role} onChanged={load} bookingFirst />
              : <div className="h-full grid place-items-center"><EmptyState title="Sélectionne un RDV posé" hint="Clique un RDV dans la liste d'un SDR pour voir sa fiche." /></div>}
        </div>
      </div>

      {addOpen && <AddRdvModal defaultDate={dateStr} onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); load(); }} />}
    </div>
  );
};

export default ProductionSdrPage;
